import React, { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const FUTURES_SYMBOLS = ["ES", "NQ", "CL", "GC", "SI", "ZB", "ZN", "RTY", "YM", "6E", "6J"];

const CHECKLIST_ITEMS = [
  {
    category: "DAILY BIAS",
    icon: "map",
    color: "blue",
    items: [
      { id: "db1", question: "Have I identified the higher timeframe direction?" },
      { id: "db2", question: "Is market structure clearly bullish or bearish?" },
      { id: "db3", question: "Am I trading WITH the trend, not against it?" },
      { id: "db4", question: "Have I checked the Daily and 4H charts?" },
    ]
  },
  {
    category: "TRAP IDENTIFICATION",
    icon: "target",
    color: "yellow",
    items: [
      { id: "ti1", question: "Has liquidity been swept (stops hunted)?" },
      { id: "ti2", question: "Is price at a valid order block?" },
      { id: "ti3", question: "Are retail traders trapped on the wrong side?" },
      { id: "ti4", question: "Is there an obvious liquidity pool ahead?" },
    ]
  },
  {
    category: "SIGNAL CONFIRMATION",
    icon: "check",
    color: "green",
    items: [
      { id: "sc1", question: "Is there a valid FU candle formation?" },
      { id: "sc2", question: "Did I WAIT for the candle to CLOSE?" },
      { id: "sc3", question: "Is there a market imbalance to fill?" },
      { id: "sc4", question: "Does the entry align with institutional flow?" },
    ]
  },
  {
    category: "MATH VALIDATION",
    icon: "calculator",
    color: "purple",
    items: [
      { id: "mv1", question: "Is my R:R at least 1:3?" },
      { id: "mv2", question: "Is my position size ≤1% of account risk?" },
      { id: "mv3", question: "Is my stop loss at a logical structure level?" },
      { id: "mv4", question: "Is my take profit at a realistic target?" },
    ]
  }
];

export default function Checklist() {
  const [symbol, setSymbol] = useState("ES");
  const [checkedItems, setCheckedItems] = useState({});
  const [showResult, setShowResult] = useState(false);

  const toggleItem = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
    setShowResult(false);
  };

  const getCategoryCompletion = (category) => {
    const items = category.items;
    const checked = items.filter(item => checkedItems[item.id]).length;
    return { checked, total: items.length, percentage: Math.round((checked / items.length) * 100) };
  };

  const getTotalCompletion = () => {
    const allItems = CHECKLIST_ITEMS.flatMap(c => c.items);
    const checked = allItems.filter(item => checkedItems[item.id]).length;
    return { checked, total: allItems.length, percentage: Math.round((checked / allItems.length) * 100) };
  };

  const total = getTotalCompletion();
  const isPassing = total.percentage === 100;

  const handleSubmitChecklist = async () => {
    setShowResult(true);
    
    if (isPassing) {
      try {
        const checklistData = {
          trade_symbol: symbol,
          items: CHECKLIST_ITEMS.flatMap(cat => 
            cat.items.map(item => ({
              id: item.id,
              category: cat.category,
              question: item.question,
              checked: checkedItems[item.id] || false
            }))
          ),
          passed: true
        };
        await axios.post(`${API}/checklists`, checklistData);
        toast.success("Checklist saved! You're cleared for entry.");
      } catch (err) {
        console.error("Failed to save checklist:", err);
      }
    }
  };

  const resetChecklist = () => {
    setCheckedItems({});
    setShowResult(false);
  };

  const getIcon = (icon) => {
    const icons = {
      map: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
      target: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      ),
      check: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      calculator: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    };
    return icons[icon];
  };

  const colorClasses = {
    blue: "text-blue-500 border-blue-500/30 bg-blue-500/10",
    yellow: "text-yellow-500 border-yellow-500/30 bg-yellow-500/10",
    green: "text-emerald-500 border-emerald-500/30 bg-emerald-500/10",
    purple: "text-purple-500 border-purple-500/30 bg-purple-500/10",
  };

  return (
    <div className="p-6 space-y-6" data-testid="checklist-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-bold text-white">SNIPER CHECKLIST</h1>
          <p className="text-sm text-slate-500 font-mono">No box unchecked. No exceptions.</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger className="w-32 bg-[#050505] border-[#262626] font-mono" data-testid="symbol-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#262626]">
              {FUTURES_SYMBOLS.map((s) => (
                <SelectItem key={s} value={s} className="font-mono">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button onClick={resetChecklist} className="btn-secondary" data-testid="reset-btn">
            RESET
          </button>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="card-institutional p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-mono text-slate-500">COMPLETION STATUS</p>
            <p className="text-3xl font-mono font-bold text-white">{total.percentage}%</p>
          </div>
          <div className={`text-sm font-mono px-4 py-2 border ${
            isPassing 
              ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-500" 
              : "border-red-500/50 bg-red-500/10 text-red-500"
          }`}>
            {isPassing ? "CLEARED FOR ENTRY" : "DO NOT ENTER"}
          </div>
        </div>
        <div className="h-2 bg-[#171717] rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ${isPassing ? "bg-emerald-500" : "bg-blue-500"}`}
            style={{ width: `${total.percentage}%` }}
          />
        </div>
        <p className="text-xs text-slate-500 mt-2 font-mono">
          {total.checked} of {total.total} criteria confirmed
        </p>
      </div>

      {/* Checklist Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {CHECKLIST_ITEMS.map((category) => {
          const completion = getCategoryCompletion(category);
          return (
            <div key={category.category} className="card-institutional">
              {/* Category Header */}
              <div className={`p-4 border-b border-[#262626] flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 border ${colorClasses[category.color]}`}>
                    {getIcon(category.icon)}
                  </div>
                  <div>
                    <h3 className="font-mono text-sm font-bold text-white">{category.category}</h3>
                    <p className="text-xs text-slate-500 font-mono">
                      {completion.checked}/{completion.total} confirmed
                    </p>
                  </div>
                </div>
                <div className={`text-xl font-mono font-bold ${
                  completion.percentage === 100 ? "text-emerald-500" : "text-slate-500"
                }`}>
                  {completion.percentage}%
                </div>
              </div>
              
              {/* Items */}
              <div className="p-4 space-y-3">
                {category.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    data-testid={`checklist-item-${item.id}`}
                    className={`w-full flex items-start gap-3 p-3 border transition-all ${
                      checkedItems[item.id]
                        ? "border-emerald-500/50 bg-emerald-500/5"
                        : "border-[#262626] hover:border-slate-600"
                    }`}
                  >
                    <div className={`w-5 h-5 border flex-shrink-0 flex items-center justify-center transition-colors ${
                      checkedItems[item.id]
                        ? "border-emerald-500 bg-emerald-500"
                        : "border-slate-600"
                    }`}>
                      {checkedItems[item.id] && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-sm text-left ${
                      checkedItems[item.id] ? "text-white" : "text-slate-400"
                    }`}>
                      {item.question}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Submit Button */}
      <div className="flex justify-center">
        <button
          onClick={handleSubmitChecklist}
          disabled={!isPassing}
          data-testid="submit-checklist-btn"
          className={`px-8 py-4 font-mono text-sm tracking-wider border transition-all ${
            isPassing
              ? "bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600 animate-pulse-glow"
              : "bg-transparent border-[#262626] text-slate-600 cursor-not-allowed"
          }`}
        >
          {isPassing ? "EXECUTE TRADE" : "COMPLETE ALL CHECKS FIRST"}
        </button>
      </div>

      {/* Result Modal */}
      {showResult && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-fade-in">
          <div className={`card-institutional p-8 max-w-md text-center ${
            isPassing ? "border-emerald-500" : "border-red-500"
          }`}>
            <div className={`w-16 h-16 mx-auto mb-4 border-2 flex items-center justify-center ${
              isPassing ? "border-emerald-500 text-emerald-500" : "border-red-500 text-red-500"
            }`}>
              {isPassing ? (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <h3 className={`font-mono text-xl font-bold mb-2 ${
              isPassing ? "text-emerald-500" : "text-red-500"
            }`}>
              {isPassing ? "CLEARED FOR ENTRY" : "TRADE DENIED"}
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              {isPassing 
                ? "All criteria met. Execute with discipline." 
                : "Missing criteria. Do NOT enter this trade. Sit on your hands."}
            </p>
            <button
              onClick={() => setShowResult(false)}
              className="btn-secondary"
            >
              {isPassing ? "UNDERSTOOD" : "I'LL WAIT"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
